// src/hooks/useEvents.ts

import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosContext } from '../contextManager/axios.context';
import { endpoints } from '../constants/endpoints';
import { useUser } from '../contextManager/user.context';
import { useToast } from '../contextManager/toast.context';

export interface CalendarEvent {
  id: string;
  tutorId: string;
  parentId: string;
  eventType: 'intro-call' | 'lesson';
  title: string;
  startAt: string; // Combined ISO 8601 format: 'YYYY-MM-DDTHH:mm:ss.sssZ'
  endAt: string;   // Combined ISO 8601 format
  seriesId?: string | null; // A unique ID to group recurring lessons
  seriesTitle?: string | null; // The user-defined name for the series
  createdAt: string;
  parentName?: string;
  tutorName?: string;
  googleMeetUrl?: string | null;     // ← add this
  parentProfileColor?: string | null;
  tutorProfilePhotoUrl?: string | null;
  feedbackText?: string | null;           // Tutor-only editable
  feedbackUpdatedAt?: string | null;
  homeworkText?: string | null;           // Tutor editable
  homeworkFileUrl?: string | null;
  homeworkUpdatedAt?: string | null;
  responseText?: string | null;           // Parent/Student editable
  responseFileUrl?: string | null;
  responseUpdatedAt?: string | null;
}

type NewEventPayload = Omit<CalendarEvent, 'id' | 'createdAt'>;

export const useEvents = () => {
    const axios = useContext(AxiosContext);
    const {userDetails} = useUser();
    const {addToast, updateToast} = useToast();
    const queryClient = useQueryClient();
    const queryKey = ['events', userDetails?.id];

    const {data: events = [], isLoading} = useQuery<CalendarEvent[]>({
        queryKey: queryKey,
        enabled: !!userDetails?.id,
        queryFn: async () => {
            const response = await axios.get(endpoints.myEvents);
            return response.data;
        }
    });

    const createEvents = useMutation({
        mutationFn: async (newEvents: NewEventPayload[]) => {
            const response = await axios.post(endpoints.createEvent, {events: newEvents})
            return response.data;
        },
        onMutate: (variables) => {
            const toastId = `event-create-${Date.now()}`;
            const title = variables.length > 1 ? `Scheduling ${variables.length} events...` : 'Scheduling event...';
            addToast({id: toastId, variant: 'loading', title});
            return {toastId};
        },
        onSuccess: (_, variables, context) => {
            queryClient.invalidateQueries({queryKey});
            const title = variables.length > 1 ? `${variables.length} events scheduled` : 'Event scheduled';
            if (context) updateToast(context.toastId, {variant: 'success', title});
        },
        onError: (_, __, context) => {
            if (context) updateToast(context.toastId, {variant: 'error', title: 'Failed to schedule event(s).'});
        },
    }).mutateAsync;

    const updateEvent = useMutation({
        mutationFn: (variables: {eventId: string; updates: Partial<CalendarEvent>}) => {
            const {eventId, updates} = variables;
            return axios.patch(endpoints.updateEvent(eventId), updates);
        },
        onMutate: () => {
            const toastId = `event-update-${Date.now()}`;
            addToast({id: toastId, variant: 'loading', title: 'Updating event...'});
            return {toastId};
        },
        onSuccess: (_, __, context) => {
            queryClient.invalidateQueries({queryKey});
            if (context) updateToast(context.toastId, {variant: 'success', title: 'Event updated.'});
        },
        onError: (_, __, context) => {
            if (context) updateToast(context.toastId, {variant: 'error', title: 'Failed to update event.'});
        }
    }).mutateAsync;

    const deleteEvent = useMutation({
        mutationFn: (eventId: string) => {
            return axios.delete(endpoints.deleteEvent(eventId));
        },
        onMutate: () => {
            const toastId = `event-delete-${Date.now()}`;
            addToast({id: toastId, variant: 'loading', title: 'Deleting event...'});
            return {toastId};
        },
        onSuccess: (_, __, context) => {
            queryClient.invalidateQueries({queryKey});
            if (context) updateToast(context.toastId, {variant: 'success', title: 'Event deleted'});
        },
        onError: (_, __, context) => {
            queryClient.invalidateQueries({queryKey});
            if (context) updateToast(context.toastId, {variant: 'error', title: 'Failed to delete event.'});
        }
    }).mutateAsync;

    return {
        events,
        isLoading, 
        createEvents, 
        updateEvent, 
        deleteEvent,
    }
}